import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Table, Button, Input, Switch, Select, Dropdown, Checkbox, Tooltip } from "antd";
import { ControlOutlined, FilterOutlined, ClearOutlined, CopyOutlined } from "@ant-design/icons";
import columns from "./columns";
import apiService from "./data";
import BundleDrawer from "../BundleDetailView/BundleDrawer";
import globalData from "../../config/global_data";
import FullPageLoader from "../common/FullPageLoader";
import { triggerNotification } from "../common/notify";
import NoDataComponent from "../common/NoDataComponent";
import { useAuth } from "../UserAuth/AuthContext";
import CreateBundle from "../BundleDetailView/CreateBundle/CreateBundle";
import { exportToExcel } from "../MasterTable/exportToExcel";
import ErrorNotification from "../common/ErrorNotification";
const { Search } = Input;
const { initialData, apidata } = apiService;


const statusMap = {
  active: { text: "Active", color: "green" },
  draft: { text: "Draft", color: "orange" },
  fail: { text: "Fail", color: "red" },
};

const getStatus = (record) => {
  const index = parseInt(record.key, 10);
  if (index % 3 === 0) return "active";
  if (index % 3 === 1) return "draft";
  return "fail";
};

const BundleTable = ({ globalList, setGlobalList }) => {
  const [alert, setAlert] = useState({});
  const [data, setData] = useState([]);
  const [originaldata, setOriginaldata] = useState();
  const [searchText, setSearchText] = useState("");
  const [selectedDrawerdata, setSelectedDrawerdata] = useState({});
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [stores, setstores] = useState([]);
  const [slectedStore, setSlectedStore] = useState("");
  const [isloader, setisloader] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState(columns.map((col) => col.dataIndex));
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [stickyHeader, setStickyHeader] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const isAnyFilterApplied = searchText || (slectedStore && slectedStore !== "Select Store") || selectedFilter;
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(100);
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, 100]);
  const tableWrapperRef = useRef(null);
  const [createDrawerVisible, setcreateDrawerVisible] = useState(false);
  const { userData } = useAuth();
  const [copiedSku, setCopiedSku] = useState(null);


  useEffect(() => {

    const handleScroll = () => {
      if (tableWrapperRef.current) {
        const scrollTop = tableWrapperRef.current.scrollTop;
        setStickyHeader(scrollTop > 0); // enable sticky only when scrolling starts
      }
    };

    const wrapper = tableWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (globalList && globalList.bundle_datalist && globalList.bundle_datalist.length > 0) {
      setData(globalList.bundle_datalist);
      setOriginaldata(globalList.bundle_datalist);
      applyAllFilters({ original: globalList.bundle_datalist })
      setisloader(false)
    }
  }, [globalList]);

  const fetchData = async () => {
    const apiData = await apidata();
    if (apiData && apiData.length > 0) {
      setData(apiData);
      setOriginaldata(apiData);
      setGlobalList({ ...globalList, bundle_datalist: apiData })

      if (drawerVisible) {
        applyAllFilters({ original: apiData });
      }
    }
    setisloader(false)
  };

  useEffect(() => {
    const intr = setInterval(() => {
      if (globalData.store_list && globalData.store_list.length > 0) {
        clearInterval(intr)
        handleStorelist();
      }
    }, 100);
    if (!globalList || !globalList.bundle_datalist || globalList.bundle_datalist.length <= 0) {
      fetchData();
    }
  }, []);
  const handleExport = () => {
    const selectedData = data.filter((item) =>
      selectedRowKeys.includes(item.key)
    );
    exportToExcel(selectedData);
  };
  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    applyAllFilters({ filter: value, store: slectedStore });
  };

  const handleStorelist = async () => {
    if (globalData.store_list.length > 0) {
      setstores(globalData.store_list);
      setSlectedStore('Select Store');
    }
  }
  const openDrawer = useCallback((bcSku, store_id) => {
    const filteredData = originaldata.find((item) =>
      item.bcSku.toLowerCase() == bcSku.toLowerCase() && item.store_id === store_id
    );

    setSelectedDrawerdata(filteredData)
    setDrawerVisible(true)
  }, [originaldata]);
  const closeDrawer = useCallback(() => setDrawerVisible(false), []);

  const handleSearch = (value) => {
    setSearchText(value);
    applyAllFilters({ search: value });
  };

  const handleColumnToggle = (dataIndex) => {
    setVisibleColumns((prev) =>
      prev.includes(dataIndex)
        ? prev.filter((col) => col !== dataIndex)
        : [...prev, dataIndex]
    );
  };

  const columnMenu = {
    items: columns.map(col => ({
      key: col.dataIndex,
      label: (
        <Checkbox
          checked={visibleColumns.includes(col.dataIndex)}
          onChange={() => handleColumnToggle(col.dataIndex)}
        >
          {col.title}
        </Checkbox>
      ),
    })),
  };
  const handleAlert = async (type, title, message, isopen) => {
    setAlert({ type, title, message, isopen });
    setTimeout(() => {
      setAlert({ isopen: false });
    }, 100);
  };
  const handleToggleActive = useCallback(async (checked, key) => {
    setisloader(true);
    let filterdata = originaldata.find(item => item.key === key);
    let responsedata = await apiService.active_inactive_status(filterdata, checked);
    if (responsedata) {
      setData(prevData =>
        prevData.map(item => item.key === key ? { ...item, active: checked } : item)
      );
      await fetchData(responsedata);
      setisloader(false);
      await handleAlert("success", "Success!", responsedata.data.message, true);

    } else {
      await fetchData(responsedata);
      setisloader(false);
      await handleAlert("error", "Fail!", "Something went wrong.", true);
    }
  }, [originaldata]);
  const updatedColumns = useMemo(() => {
    return columns
      .filter(col => visibleColumns.includes(col.dataIndex))
      .map((col) => {
        if (col.dataIndex === "active") {
          return {
            ...col,
            render: (_, record) => (
              <Switch
                checked={record.active}
                onChange={async (checked) => await handleToggleActive(checked, record.key)}
              />
            )
          };
        }
        if (col.dataIndex === "bcSku") {
          return {
            ...col,
            render: (_, record) => (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  const icon = e.currentTarget.querySelector('.copy-icon');
                  if (icon) icon.style.opacity = 1;
                }}
                onMouseLeave={(e) => {
                  const icon = e.currentTarget.querySelector('.copy-icon');
                  if (icon) icon.style.opacity = 0;
                }}
              >
                <Button type="link" onClick={(e) => openDrawer(record.bcSku, record.store_id)}>
                  {record.bcSku ? record.bcSku : "Update"}
                </Button>
                <Tooltip title={copiedSku === record.bcSku ? "Copied" : "Copy"}>
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => {
                      navigator.clipboard.writeText(record.bcSku);
                      setCopiedSku(record.bcSku);
                      setTimeout(() => setCopiedSku(null), 1500);
                    }}
                    style={{
                      marginLeft: 6,
                      color: copiedSku === record.bcSku ? '#52c41a' : '#999',
                      cursor: 'pointer',
                      opacity: 0,
                      transition: 'opacity 0.3s ease, color 0.3s ease',
                    }}
                  />
                </Tooltip>
              </span>
            )
          };
        }
        return col;
      });
  }, [handleToggleActive, visibleColumns, openDrawer, copiedSku]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };
  const handleStoreChange = (value) => {
    setSlectedStore(value);
    applyAllFilters({ store: value });
  };
  const activeFiltersCount = [
    searchText !== '',
    slectedStore && slectedStore !== 'all' && slectedStore !== 'Select Store',
    selectedFilter !== null,
  ].filter(Boolean).length;
  const applyAllFilters = async (filters = {}) => {
    const {
      search = searchText,
      store = slectedStore,
      filter = selectedFilter,
      priceRange = selectedPriceRange,
      original = originaldata,
    } = filters;

    let filtered = await original;

    // Filter by store
    if (store !== "" && store !== "all" && store !== "Select Store") {
      filtered = filtered.filter(item =>
        item.store.toLowerCase().includes(store.toLowerCase())
      );
    }

    // Filter by search text
 

    if (search !== "") {
      filtered = await filtered.filter(item =>
        item.bcSku.toLowerCase().includes(search.toLowerCase()) ||
        item.bc_name.toLowerCase().includes(search.toLowerCase())
      );
    }
    switch (filter) {
      case "All":
        setSelectedPriceRange([minPrice, maxPrice]);
        // filtered = originaldata
        break;
      case "Products not live":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => item.active === 0);
        break;
      case "Products live":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => item.active === 1);
        break;
      case "Enrichment pending":
        setSelectedPriceRange([minPrice, maxPrice]);
        filtered = filtered.filter(item => item.enrichment === false);
        break;
      default:
        break;
    }
    setData(filtered);
    // setCurrentPage(1);
  };
  const createBundleOpenDrawer = useCallback((key) => {

    setcreateDrawerVisible(true);

  });

  const createBundleCloseDrawer = () => setcreateDrawerVisible(false);
  return (
    <div>
      {/* Search and Filter Bar */}
      <div style={{ marginBottom: 16 }} className="TableFilterBox">

        <Select
          placeholder="Filter"
          style={{ width: 180 }}
          suffixIcon={<FilterOutlined style={{ color: "rgba(0, 0, 0, 0.88)" }} />}
          value={selectedFilter}
          className="filterSelect"
          onChange={handleFilterChange}
          options={[
            { value: "All", label: "All" },
            { value: "Products not live", label: "Products not live" },
            { value: "Products live", label: "Products live" },
            { value: "Enrichment pending", label: "Enrichment pending" },
          ]}
        />
        <Select
          placeholder="Stores"
          style={{ width: 120 }}
          value={slectedStore}
          options={stores}
          onChange={handleStoreChange}
        />
        <Search
          placeholder="Search by BC SKU or Name"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 270 }}
          allowClear
          enterButton
        />
        {isAnyFilterApplied && (
          <Button
            onClick={() => {
              setSearchText('');
              setSlectedStore('Select Store');
              setSelectedFilter(null);
              setSelectedPriceRange([minPrice, maxPrice]); // Reset slider
              setData(originaldata);
              // setCurrentPage(1); 
            }}
            className="ClearAllFiltersBtn"

          >
            <ClearOutlined style={{ marginRight: 4 }} />
            {activeFiltersCount === 1 ? 'Clear Filters' : 'Clear Filters'}
          </Button>
        )}
        <Dropdown menu={{ ...columnMenu, className: 'ColumnMenuClass' }} trigger={["click"]} placement="bottomRight" className="TableSettings">
          <Button icon={<ControlOutlined />} />
        </Dropdown>
        <div className="RightBox" style={{ display: "flex", gap: 8 }}>
          <Button type="primary" disabled={selectedRowKeys.length <= 0} onClick={handleExport}>
            Export Selected
          </Button>
        </div>
      </div>
      <FullPageLoader isloader={isloader} />
      <ErrorNotification {...alert} />
      {/* Data Table */}
      <div
        className="TableBox"
        ref={tableWrapperRef}
        style={{ maxHeight: 700, overflow: 'auto' }}
      >
        {data && data.length > 0 ?
          <Table
            columns={updatedColumns}
            dataSource={data}
            scroll={{ x: '100%', y: 500 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: data.length,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              showTotal: (total) =>
                `Total ${total} items${selectedRowKeys.length > 0 ? ` | Selected: ${selectedRowKeys.length}` : ''}`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              position: ['bottomLeft']
            }}
            rowSelection={rowSelection}
            bordered
            sticky={stickyHeader ? { offsetHeader: 0 } : false}
          /> : <div>
            <NoDataComponent />
          </div>}
      </div>

      {/* Side Drawer */}
      {drawerVisible && <BundleDrawer onProductCreate={fetchData} visible={drawerVisible} onClose={closeDrawer} selected_prod_data={selectedDrawerdata} setisloader={setisloader} globalList={globalList} setGlobalList={setGlobalList}/>}
      {createDrawerVisible && <CreateBundle visible={createDrawerVisible} onClose={createBundleCloseDrawer} onProductCreate={fetchData} setisloader={setisloader} globalList={globalList} setGlobalList={setGlobalList}/>}
    </div>
  );
};

export default BundleTable;
